/**
 * Feature Flags Service
 *
 * API service layer for feature flags operations.
 * Handles all HTTP requests to the backend feature flags API.
 *
 * Read operations (both console and frontend):
 * - getAllFlags(): Fetch simple key-value map
 * - getAllDetailed(): Fetch detailed feature flags with metadata
 * - getFlag(): Check if a specific flag is enabled
 *
 * Admin operations (console only, tree-shaken in frontend):
 * - updateFlag(): Update a feature flag's state
 */

import type { AxiosInstance } from 'axios'
import type {
  FeaturesMapResponse,
  FeatureDetailResponse,
  FeatureFlagState,
  UpdateFeatureFlagRequest,
  FeaturesMap
} from './types'

/**
 * Feature Flags Service
 *
 * Provides type-safe API calls to the backend feature flags endpoints.
 * Uses dependency injection for the axios instance.
 */
export class FeatureFlagsService {
  constructor(private axios: AxiosInstance) {}

  // =============================================================================
  // READ OPERATIONS (both console and frontend)
  // =============================================================================

  /**
   * Get all feature flags as a simple key-value map
   *
   * Endpoint: GET /features
   * Returns: { "features": { "key": boolean } }
   *
   * Use this for lightweight flag checks when you don't need metadata.
   * Backend caches this response in Redis (180s TTL).
   *
   * @returns Map of feature flag keys to enabled/disabled state
   * @throws API error if request fails
   *
   * @example
   * const flags = await service.getAllFlags()
   * // { "communication_v2_enabled": true, "new_ui": false }
   */
  async getAllFlags(): Promise<FeaturesMap> {
    const response = await this.axios.get<FeaturesMapResponse>('/features')
    return response.data.features ?? {}
  }

  /**
   * Get all feature flags with full details
   *
   * Endpoint: GET /features/detailed
   * Returns: Array of FeatureDetailResponse
   *
   * Use this in admin UI when you need to display/edit flag metadata
   * (description, expiration, created_by, etc.)
   *
   * @returns Array of detailed feature flags
   * @throws API error if request fails
   *
   * @example
   * const flags = await service.getAllDetailed()
   * // [{ id: 1, key: "communication_v2_enabled", enabled: true, ... }]
   */
  async getAllDetailed(): Promise<FeatureDetailResponse[]> {
    const response = await this.axios.get<FeatureDetailResponse[]>('/features/detailed')
    return response.data ?? []
  }

  /**
   * Get a specific feature flag's enabled state
   *
   * Endpoint: GET /features/:key
   * Returns: { "enabled": boolean }
   *
   * Use this to check a single flag without fetching all flags.
   * Useful for on-demand checks.
   *
   * @param key - Feature flag key (snake_case)
   * @returns True if flag is enabled, false otherwise
   * @throws API error if flag not found or request fails
   *
   * @example
   * const enabled = await service.getFlag('communication_v2_enabled')
   * // true
   */
  async getFlag(key: string): Promise<boolean> {
    const response = await this.axios.get<FeatureFlagState>(`/features/${key}`)
    return response.data.enabled ?? false
  }

  // =============================================================================
  // ADMIN OPERATIONS (console only, tree-shaken in frontend)
  // =============================================================================

  /**
   * Update a feature flag (admin only)
   *
   * Endpoint: PATCH /features/:key
   * Body: { enabled: boolean, description: string, expiresAt: ISO string, rolloutPercentage: number }
   *
   * Requires admin authentication. Updates the flag's state and metadata.
   * Backend validates permissions and logs audit trail.
   *
   * Tree-shaking: This method is only included in console bundle.
   * Frontend apps that don't import useFeatureFlagsAdmin won't include this code.
   *
   * @param key - Feature flag key (snake_case)
   * @param request - Update request with enabled state, description, expiration, and rollout percentage
   * @returns Updated feature flag with full details
   * @throws API error if unauthorized, not found, or validation fails
   *
   * @example
   * const updated = await service.updateFlag('communication_v2_enabled', {
   *   enabled: true,
   *   description: 'Enable new communication system',
   *   expiresAt: '2025-12-31T23:59:59Z',
   *   rolloutPercentage: 100
   * })
   */
  async updateFlag(key: string, request: UpdateFeatureFlagRequest): Promise<FeatureDetailResponse> {
    const response = await this.axios.patch<FeatureDetailResponse>(`/features/${key}`, request)
    return response.data
  }
}
